/**
 * Simple analytics utility for tracking user interactions
 * Can be extended to integrate with Google Analytics, Mixpanel, etc.
 */

interface AnalyticsEvent {
  name: string;
  properties: Record<string, any>;
}

class Analytics {
  private events: AnalyticsEvent[];
  private sessionStart: number;
  private enabled: boolean;

  constructor() {
    this.events = [];
    this.sessionStart = Date.now();
    this.enabled = process.env.NODE_ENV === 'production';
  }

  track(eventName: string, properties: Record<string, any> = {}): void {
    const event: AnalyticsEvent = {
      name: eventName,
      properties: {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionDuration: Date.now() - this.sessionStart,
        userAgent: navigator.userAgent,
        url: window.location.href,
      },
    };

    this.events.push(event);

    // Console log in development
    if (!this.enabled) {
      console.log('📊 Analytics Event:', eventName, properties);
    }

    // Send to backend or analytics service
    this._sendToBackend(event);
  }

  private async _sendToBackend(event: AnalyticsEvent): Promise<void> {
    if (!this.enabled) return;

    try {
      // In production, send to your analytics endpoint
      await fetch('/api/analytics/track', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(event),
      });
    } catch (error) {
      console.error('Failed to send analytics event:', error);
    }
  }

  // Track page views
  pageView(pageName: string): void {
    this.track('Page View', { pageName });
  }

  // Track user interactions
  interaction(interactionType: string, details: Record<string, any> = {}): void {
    this.track('User Interaction', { interactionType, ...details });
  }

  // Track errors
  error(errorType: string, errorMessage: string, errorStack: string | null = null): void {
    this.track('Error', { errorType, errorMessage, errorStack });
  }

  // Track performance metrics
  performance(metricName: string, value: number, unit: string = 'ms'): void {
    this.track('Performance', { metricName, value, unit });
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Convenience functions
export const trackEvent = (eventName: string, properties?: Record<string, any>) => analytics.track(eventName, properties);
export const trackPageView = (pageName: string) => analytics.pageView(pageName);
export const trackInteraction = (type: string, details?: Record<string, any>) => analytics.interaction(type, details);
export const trackError = (type: string, message: string, stack?: string | null) => analytics.error(type, message, stack ?? null);
export const trackPerformance = (metric: string, value: number, unit?: string) => analytics.performance(metric, value, unit);
